import { mount, unmount } from 'redom';
import {
  displayElementAroundAnchorSticky
} from 'utilities/element-position';

import CanvasTextToolbar from 'generics/components/basic-text-editor-slate/toolbar-native/canvas-text';

const containerId = 'dialogs-mount-point';

let toolbar = null;
let scrollUnlistener = null;

class TextToolbar {

  static show(canvas, configuration) {

    const mountPoint = canvas.parentNode.parentNode;

    if (toolbar !== null) {
      unmount(mountPoint, toolbar);
    }

    const scrollableParent = canvas.closest('.basic-text-editor-main');

    mount(mountPoint, toolbar = new CanvasTextToolbar(buildToobarConfiguration(configuration), scrollableParent));

    const element = toolbar.el;
    const anchorElement = configuration.anchor;
    const viewportElement = void 0;
    const verticalOffset = 0;
    const horizontalOffset = 0;
    const startSide = 'top';
    const placeOnCenter = true;
    const positionableSides = [
      'top',
      'bottom',
      'left',
      'right'
    ];

    scrollUnlistener = displayElementAroundAnchorSticky(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides, scrollableParent);

  }

  static hide(canvas) {

    if (toolbar !== null) {
      unmount(toolbar.el.parentNode, toolbar);
      scrollUnlistener();
    }

    toolbar = null;

  }

}

function normalizeLineHeight(fontSize, sliderValue) {
  const minLineHeight = 1.0; // minimum CSS-like line height
  const maxLineHeight = 2.5; // maximum CSS-like line height
  const cssLineHeight = minLineHeight + ((maxLineHeight - minLineHeight) * (sliderValue / 100));
  return cssLineHeight * (16 / fontSize);
}

function buildToobarConfiguration(configuration) {

  const canvas = configuration.canvas;
  const fabricCanvas = configuration.fabricCanvas;
  const activeObject = configuration.activeObject;

  function postChange() {
    fabricCanvas.renderAll();
    fabricCanvas.fire('object:modified', { target: activeObject });
  }

  const toolbarConfiguration = {
    anchor: configuration.anchor,
    heading: {
      h1: false,
      h2: false,
      h3: false,
      h4: false,
      h5: false,
      h6: false
    },
    marks: {
      bold: activeObject.fontWeight === 'bold',
      italic: activeObject.fontStyle === 'italic',
      underline: activeObject.underline,
      strikeThrough: activeObject.linethrough
    },
    color: {
      text: activeObject.fill,
      background: activeObject.backgroundColor
    },
    font: {
      family: activeObject.fontFamily,
      size: +activeObject.fontSize || 20,
      weight: activeObject.fontWeight
    },
    lineHeight: activeObject.lineHeight,
    alignment: {
      left: activeObject.textAlign === 'left',
      center: activeObject.textAlign === 'center',
      right: activeObject.textAlign === 'right'
    },
    shadow: activeObject.shadow || {
      color: '', // Shadow color
      blur: 0, // Shadow blur
      offsetX: 0, // Horizontal offset
      offsetY: 0
    },
    callbacks: {
      onH1Change: (value) => {},
      onH2Change: (value) => {},
      onH3Change: (value) => {},
      onH4Change: (value) => {},
      onH5Change: (value) => {},
      onH6Change: (value) => {},
      onBoldChange: (value) => {
        if (value === true) {
          activeObject.set({ fontWeight: 'bold' });
        } else {
          activeObject.set({ fontWeight: 'normal' });
        }
        postChange();
      },
      onItalicChange: (value) => {
        if (value === true) {
          activeObject.set({ fontStyle: 'italic' });
        } else {
          activeObject.set({ fontStyle: 'normal' });
        }
        postChange();
      },
      onUnderlineChange: (value) => {
        activeObject.set({ underline: value });
        postChange();
      },
      onStrikeThroughChange: (value) => {
        activeObject.set({ linethrough: value });
        postChange();
      },
      onTextColorChange: (value) => {
        activeObject.set({ fill: value });
        postChange();
      },
      onBackgroundColorChange: (value) => {
        activeObject.set({ backgroundColor: value });
        postChange();
      },
      onFontFamilyChange: (fontFamily) => {
        activeObject.set({ fontFamily });
        canvas.addFontFamily(fontFamily);
        postChange();
      },
      onFontSizeChange: (value) => {
        activeObject.set({ fontSize: value });
        postChange();
      },
      onFontWeightChange: (value) => {
        activeObject.set({ fontWeight: value });
        postChange();
      },
      onLineHeightChange: (value) => {
        activeObject.set({
          lineHeight: normalizeLineHeight(activeObject.fontSize, +value)
        });
        postChange();
      },
      onAlignmentLeftChange: (value) => {
        if (value === true) {
          activeObject.set({ textAlign: 'left' });
        } else {
          activeObject.set({ textAlign: 'center' });
        }
        postChange();
      },
      onAlignmentCenterChange: (value) => {
        if (value === true) {
          activeObject.set({ textAlign: 'center' });
        } else {
          activeObject.set({ textAlign: 'left' });
        }
        postChange();
      },
      onAlignmentRightChange: (value) => {
        if (value === true) {
          activeObject.set({ textAlign: 'right' });
        } else {
          activeObject.set({ textAlign: 'center' });
        }
        postChange();
      },
      onShadowChange: (shadow) => {
        activeObject.set({
          shadow: {
            color: shadow.color,
            blur: shadow.blur,
            offsetX: shadow.offsetX,
            offsetY: shadow.offsetY
          }
        });
        postChange();
      },
      onMoveForward() {
        fabricCanvas.bringObjectForward(activeObject);
        postChange();
      },
      onMoveBackward() {
        fabricCanvas.sendObjectBackwards(activeObject);
        postChange();
      },
      onMoveToFront() {
        fabricCanvas.bringObjectToFront(activeObject);
        postChange();
      },
      onMoveToBack() {
        fabricCanvas.sendObjectToBack(activeObject);
        postChange();
      }
    },
    onClose() {
      canvas.enableDeleting();
    },
    onActive() {
      canvas.disableDeleting();
    },
    onInactive() {
      canvas.enableDeleting();
    },
    onDuplicate() {
      canvas.duplicateActiveObject();
    }
  };

  return toolbarConfiguration;

}

export default TextToolbar;